import { delay } from 'redux-saga'
import { all, call, put, select } from 'redux-saga/effects'
import { addError, removeError } from './modules/errors'
import { setLoading, setLoadingSuccess, unsetLoading } from './modules/loading'
import ERROR_MSGS from 'portal-modules/auth/constants/error-message'
import SERVER_FIELDS from '../constants/server-fields'
import { updatePermissionNoticeStatus } from './modules/permissionNotice'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'
const { isTP, isRH, isNotRP } = getProjectConditions()
const isDev = process.env.NODE_ENV === 'development'
const SAGA_SKIPPED_READ_ONLY = 'rp/create-request-saga/SAGA_SKIPPED_READ_ONLY'
import { DATABASE_MAINTENANCE } from 'portal-modules/common/constants/urls'
import {
  TP_INVITE_USER,
  LANGUAGE_SET_FROM_NORMAL_LOGIN,
} from 'portal-modules/common/constants/keys'
const DATABASE_MAINTENANCE_CODE = '503'

export function* handleResponseError(
  { code, fields = [], message },
  { key, action: { onError } = {} },
) {
  // handle unkeyed fields
  let unkeyed = fields.filter(field => !field.field_name)
  yield all(
    unkeyed.map(field => put(addError({ key, message: field.message }))),
  )

  switch (code) {
    case 1002: {
      let gateway_timeout_error = fields.filter(
        field => field.field_name == 'gateway_timeout_error',
      )
      // for form field validation errors
      if (typeof onError === 'function') {
        // call the onError provided by Form & pass fields as { [name]: err }
        yield call(
          onError,
          fields.reduce((obj, { field_name, message }) => {
            let name = SERVER_FIELDS[field_name] || field_name
            return { ...obj, [name]: message }
          }, {}),
        )
      } else if (key === TP_INVITE_USER && isTP) {
        yield put(
          addError({ key, message: { fields, message: ERROR_MSGS[1002] } }),
        )
      } else if (key === 'payments' && gateway_timeout_error.length) {
        yield put(
          addError({
            key,
            message: { fields, message: gateway_timeout_error[0].message },
          }),
        )
      } else {
        // save the fields + message in store for manual handling
        yield put(addError({ key, message: { fields, message } }))
      }
      break
    }
    default:
      // for all other errors save the message corresponding to the provided error code

      const hasLanguageSet =
        localStorage.getItem(LANGUAGE_SET_FROM_NORMAL_LOGIN) || isNotRP
      const errorMessage =
        ERROR_MSGS[code] || message || __('Something went wrong.')

      yield put(
        addError({
          key,
          message: hasLanguageSet
            ? errorMessage
            : message || __('Something went wrong.'),
        }),
      )
      break
  }
}

export default (
  reqFn,
  {
    key,
    message,
    successMessage,
    duration = 1000,
    onError = handleResponseError,
    write = false,
    showLoading = true,
  },
) =>
  function* createRequestSaga(action) {
    let readOnly = yield select(({ user }) => user.readOnly)

    //For tenant portal remove the readOnly functionality and switch over permission based approach
    if (isTP || isRH) {
      readOnly = false
    }

    let successful = false
    // disable write actions in readOnly mode
    if (!write || !readOnly) {
      const loadingData = successMessage
        ? { key, message, successMessage }
        : { key, message }
      // always setLoading and remove any errors at the given key
      yield all([
        put(removeError(key)),
        put(showLoading && setLoading(loadingData)),
      ])
      // attempt to call the request function, passing it the redux action
      try {
        yield delay(500)
        yield call(reqFn, action)
        successful = true
      } catch (err) {
        // call a custom error handler if passed, otherwise use default
        // send err (axios-specific structure), key, action objectt
        const statusCode = err.response ? err.response.status : ''
        if (
          statusCode &&
          statusCode == DATABASE_MAINTENANCE_CODE &&
          location.pathname != DATABASE_MAINTENANCE
        )
          window.location.href = DATABASE_MAINTENANCE
        yield call(onError, err.response ? err.response.data : err, {
          key,
          action,
        })
      }

      yield showLoading && delay(duration)
      // set success or clear loader (depending on type)
      if (successful && successMessage) {
        yield put(showLoading && setLoadingSuccess({ key }, action))
      } else {
        yield put(showLoading && unsetLoading({ key }))
      }
    } else {
      // notify that we skipped the saga in dev mode (for clearer debugging/time travel)
      yield put({ type: SAGA_SKIPPED_READ_ONLY })
      yield put(updatePermissionNoticeStatus(true))
    }
  }
